// ================================================
// CAMADA DE CONTEÚDO
// ================================================
// Fonte única de todo o texto editorial do site.
//
// Prioridade:
//   1. API (runtime, quando o painel estiver no ar)
//   2. Conteúdo embutido no build  ← defaults.js
//   3. Nada: `defaults` é sempre o piso, nunca falha
//
// Enquanto o painel não existe, só o nível 2 está ativo — o site
// renderiza exatamente como antes, sem requisição nenhuma.
//
// Componentes NUNCA leem defaults.js direto: usam getBlock(), para
// que ligar a API depois não exija tocar em componente nenhum.

import { useReducer, useEffect, useMemo } from 'react';
import { defaults } from './defaults';
import { services as servicosPadrao } from '../data';

/**
 * Mescla `override` sobre `base`.
 *
 * Objetos mesclam campo a campo; arrays substituem por inteiro.
 * A substituição é intencional: um array de 3 estatísticas precisa
 * poder virar 4, e mesclar por índice deixaria sobras do array antigo.
 */
function deepMerge(base, override) {
  if (override === undefined || override === null) return base;
  if (Array.isArray(override)) return override;
  if (typeof override !== 'object') return override;
  if (typeof base !== 'object' || base === null || Array.isArray(base)) return override;

  const out = { ...base };
  for (const key of Object.keys(override)) {
    out[key] = deepMerge(base[key], override[key]);
  }
  return out;
}

// Sobreposição vinda da API, aplicada por applyOverrides().
// Começa vazia: o primeiro render usa os defaults embutidos, que
// é o que o Google indexa. A API chega logo depois e atualiza.
let overrides = {};

// Componentes que precisam re-renderizar quando a API responde
const assinantes = new Set();

/** Registra um callback para quando o conteúdo da API chegar. */
export function inscrever(fn) {
  assinantes.add(fn);
  return () => assinantes.delete(fn);
}

/**
 * Retorna um bloco de conteúdo já mesclado.
 *
 * Chave ausente devolve objeto vazio em vez de `undefined`, para o
 * componente nunca quebrar com "cannot read property of undefined".
 *
 * @param {string} key  ex.: 'home.hero', 'global.contact'
 */
export function getBlock(key) {
  const base = defaults[key];
  if (base === undefined && overrides[key] === undefined) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[content] bloco desconhecido: "${key}"`);
    }
    return {};
  }
  return deepMerge(base ?? {}, overrides[key]);
}

/** Aplica conteúdo vindo da API por cima dos defaults. */
export function applyOverrides(data) {
  overrides = data && typeof data === 'object' ? data : {};
  assinantes.forEach((fn) => fn());
}

/** Serviços atuais: os da API quando já chegaram, senão os embutidos. */
export function getServicos() {
  return Array.isArray(servicosApi) ? servicosApi : servicosPadrao;
}

let servicosApi = null;

/**
 * Busca o conteúdo mais recente da API e sobrepõe aos defaults.
 *
 * Silencioso por design: se a API estiver fora, o site continua
 * exibindo o conteúdo embutido no build. Nada de tela de erro
 * para o visitante por causa do painel.
 */
export function carregarConteudoRemoto() {
  // Não faz sentido no painel (ele já fala com a API direto)
  if (typeof window === 'undefined') return;

  fetch('/api/content', { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then((corpo) => {
      if (!corpo || corpo.ok !== true || !corpo.data) return;

      if (Array.isArray(corpo.data.services) && corpo.data.services.length > 0) {
        servicosApi = corpo.data.services;
      }
      if (corpo.data.blocks && typeof corpo.data.blocks === 'object') {
        applyOverrides(corpo.data.blocks);
      } else {
        assinantes.forEach((fn) => fn());
      }
    })
    .catch(() => {
      /* site segue com o conteúdo do build */
    });
}

/**
 * Hook para componentes que devem refletir edições do painel
 * assim que a API responde.
 *
 * Devolve o bloco já mesclado e re-renderiza quando o conteúdo
 * remoto chega. Componentes estáticos podem seguir usando
 * getBlock() direto — só não atualizam sem recarregar a página.
 */
export function useBloco(chave) {
  const [versao, forcar] = useReducer((n) => n + 1, 0);
  useEffect(() => inscrever(forcar), []);
  // Memoizado por chave+versão: o deepMerge devolve um objeto novo
  // a cada chamada, o que faria efeitos dependentes rodarem em loop.
  return useMemo(() => getBlock(chave), [chave, versao]);
}

/** Como useBloco, mas para a lista de serviços. */
export function useServicos() {
  const [versao, forcar] = useReducer((n) => n + 1, 0);
  useEffect(() => inscrever(forcar), []);
  return useMemo(() => getServicos(), [versao]);
}

/**
 * Monta a URL do WhatsApp a partir do conteúdo editável.
 *
 * Substitui o waUrl() antigo de src/data: agora o número vem do
 * painel, então trocar de telefone não exige mexer no código.
 */
export function urlWhatsapp(canal = 'comercial', mensagem) {
  const d = getBlock('global.dados');
  const numero =
    canal === 'administrativo'
      ? d.whatsappAdmin || d.whatsappComercial
      : d.whatsappComercial;
  const texto = mensagem || d.whatsappMensagem || '';
  return `https://api.whatsapp.com/send?phone=${numero}&text=${encodeURIComponent(texto)}`;
}

/** Dados de contato já normalizados para os componentes. */
export function useDados() {
  const d = useBloco('global.dados');
  return {
    ...d,
    emails: Array.isArray(d.emails) ? d.emails : [],
    endereco: {
      rua: d.enderecoRua || '',
      cidade: d.enderecoCidade || '',
      cep: d.enderecoCep || '',
    },
  };
}

/** Perguntas do FAQ. */
export function useFaqs() {
  const b = useBloco('data.faqs');
  return Array.isArray(b.itens) ? b.itens : [];
}

/** Parceiros do carrossel. */
export function useParceiros() {
  const b = useBloco('data.partners');
  return Array.isArray(b.itens) ? b.itens : [];
}

/**
 * Cidades por estado, no formato que o BrazilMap espera:
 * { RO: { label, desc, cities: [{ n, c:[lon,lat], hq }] } }
 *
 * A conversão acontece aqui para o painel poder usar campos
 * separados (lon/lat) em vez de um array de dois números, que
 * seria confuso de editar num formulário.
 */
export function useMapa() {
  const b = useBloco('data.mapa');

  // Memoizado: sem isto o objeto seria recriado a cada render e
  // os efeitos que dependem dele rodariam em loop.
  return useMemo(() => {
    const estados = Array.isArray(b.estados) ? b.estados : [];
    const saida = {};
    for (const e of estados) {
      if (!e || !e.uf) continue;
      saida[e.uf] = {
        label: e.label || e.uf,
        desc: e.desc || undefined,
        cities: (Array.isArray(e.cidades) ? e.cidades : [])
          .filter((c) => c && c.n)
          .map((c) => ({
            n: c.n,
            c: [Number(c.lon) || 0, Number(c.lat) || 0],
            hq: !!c.hq,
          })),
      };
    }
    return saida;
  }, [b]);
}

// Ainda usados pelo Sobre e por componentes não migrados
export { team, pillars, testimonials } from '../data';
